import { Button, Card, ConfirmDialog, useToast } from '@citizen-walk/shared-ui';
import type { DistributeVerificationInput } from '@citizen-walk/shared-types';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { useDistributeVerification } from './useDistributeVerification';
import { useVerificationTemplates } from './useVerificationTemplates';

/**
 * AssignmentSet 配分実行ページ (Sprint AZ、Phase 5 REQ-4.3.20〜25)。
 *
 * @deprecated 2026-09-04: 後継は `VerificationAssignmentsPage` (`/verification/assignmentSets`、
 *   予約 + PostPicker/MembershipPicker 付きの新規作成)。サイドバーの導線は後継に付け替え済で、
 *   本画面はルート (`/verification/distribute`) だけ残した後方互換用。callable
 *   `distributeVerification` は `sessionIds: []` 固定で対象セッション機能に対応していない。
 *
 * テンプレ + 投稿 ID 群 + 配分先 user UID 群 + 戦略 + constraints を form 入力 →
 * ConfirmDialog (「N 件配分予定」) を経て distributeVerification callable を invoke。
 * 結果 (assignmentSetId / assignmentCount / perUserCount) を inline 表示。
 *
 * 認可: master / district admin (担当地区) のみ。callable 側でも guard。
 *
 * minimum scope:
 *   - postIds は textarea / chip 風 ID 直接入力 (本格的な投稿ピッカーは別 sprint)
 *   - userIds も textarea 直接入力 (district 参加者 picker UI は別 sprint)
 *   - all = "{全選択}" の概念は callable 側で展開しない (UI 側で投稿 / user リストを fetch して
 *     用意する必要があるが、Phase 5 minimum 規模では admin が手動入力で十分)
 */

type Strategy = 'round-robin' | 'random' | 'all-users';

const STRATEGY_OPTIONS: { value: Strategy; label: string; help: string }[] = [
  {
    value: 'round-robin',
    label: 'round-robin (均等配分)',
    help: 'postIds を userIds に順番に均等配分。各 post は minAssignmentsPerPost 件以上の user に。',
  },
  {
    value: 'random',
    label: 'random (シャッフル配分)',
    help: 'postIds をシャッフルしてから round-robin。',
  },
  {
    value: 'all-users',
    label: 'all-users (全員に全投稿)',
    help: '全 post × 全 user の cross product (上限制御推奨)。',
  },
];

function parseIds(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function AssignmentSetGeneratorPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const { state: authState } = useAdminAuth();
  const districtState = useDistrict(districtId ?? null);
  const templatesState = useVerificationTemplates(districtId ?? null);
  const { toast } = useToast();
  const { state: distState, call: callDistribute, reset } = useDistributeVerification();

  const [templateId, setTemplateId] = useState<string>('');
  const [postIdsText, setPostIdsText] = useState('');
  const [userIdsText, setUserIdsText] = useState('');
  const [strategy, setStrategy] = useState<Strategy>('round-robin');
  const [maxPerUserText, setMaxPerUserText] = useState('');
  const [minPerPostText, setMinPerPostText] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canManage = isMaster || isDistrictAdminOfThis;

  const templates = templatesState.status === 'ready' ? templatesState.items : [];
  const postIds = useMemo(() => parseIds(postIdsText), [postIdsText]);
  const userIds = useMemo(() => parseIds(userIdsText), [userIdsText]);

  const maxPerUser = maxPerUserText === '' ? undefined : Number(maxPerUserText);
  const minPerPost = minPerPostText === '' ? undefined : Number(minPerPostText);

  const validationError = ((): string | null => {
    if (!templateId) return 'テンプレートを選択してください';
    if (postIds.length === 0) return 'postIds を 1 件以上入力してください';
    if (userIds.length === 0) return 'userIds を 1 件以上入力してください';
    if (maxPerUser !== undefined && (!Number.isFinite(maxPerUser) || maxPerUser <= 0)) {
      return 'maxAssignmentsPerUser は正の整数';
    }
    if (minPerPost !== undefined && (!Number.isFinite(minPerPost) || minPerPost <= 0)) {
      return 'minAssignmentsPerPost は正の整数';
    }
    return null;
  })();

  /** 配分予定件数 (UI 表示用、actual な配分は callable 側で再計算)。 */
  const estimatedCount = useMemo(() => {
    if (validationError) return 0;
    if (strategy === 'all-users') {
      const raw = postIds.length * userIds.length;
      return maxPerUser ? Math.min(raw, maxPerUser * userIds.length) : raw;
    }
    // round-robin / random: 各 post を min (default 1) 件配る
    return postIds.length * (minPerPost ?? 1);
  }, [validationError, strategy, postIds.length, userIds.length, maxPerUser, minPerPost]);

  function handleSubmit() {
    if (validationError) {
      toast('error', validationError);
      return;
    }
    setConfirmOpen(true);
  }

  async function handleConfirm() {
    setConfirmOpen(false);
    if (!districtId) return;
    const input: DistributeVerificationInput = {
      districtId,
      templateId,
      postIds,
      distribution: { strategy, userIds },
      ...(maxPerUser !== undefined || minPerPost !== undefined
        ? {
            constraints: {
              ...(maxPerUser !== undefined ? { maxAssignmentsPerUser: maxPerUser } : {}),
              ...(minPerPost !== undefined ? { minAssignmentsPerPost: minPerPost } : {}),
            },
          }
        : {}),
      ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
      ...(description ? { description } : {}),
    };
    const res = await callDistribute(input);
    if (res) {
      toast('success', `配分完了: ${res.assignmentCount} 件 (set: ${res.assignmentSetId})`);
    } else {
      toast('error', `配分失敗: ${distState.error?.message ?? 'unknown'}`);
    }
  }

  return (
    <>
      <header className="mb-6">
        <Link
          to={districtId ? `/districts/${districtId}` : '/districts'}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← 地区詳細
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">
          AI 検証 配分実行 (AssignmentSet generator)
          {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          REQ-4.3.20〜25 — テンプレ + 投稿 + 配分先 user + 戦略 を指定し、 distributeVerification
          callable で AssignmentSet を新規作成します (rate limit: 1/min)。
        </p>
      </header>

      {/* 2026-09-04: 旧画面。導線はサイドバーから外し、後継 (配分セット一覧) へ誘導する。 */}
      <Card padding="md" className="border-warning-600 mb-4">
        <p className="text-sm text-warning-800">
          この画面は旧版です。投稿・参加者を一覧から選べる新しい配分画面と、セッション終了後にまとめて配る
          「配分の予約」は
          <Link
            to={districtId ? `/districts/${districtId}/verification/assignmentSets` : '/districts'}
            className="mx-1 text-primary-700 underline"
          >
            検証の配分
          </Link>
          からご利用ください。
        </p>
      </Card>

      {!canManage && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">
            この地区の配分実行権限がありません (role: {authState.role ?? 'none'})。
          </p>
        </Card>
      )}

      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">1. テンプレート選択</h2>
        {templatesState.status === 'loading' && (
          <p className="text-sm text-slate-600">テンプレ読込中…</p>
        )}
        {templatesState.status === 'error' && (
          <p className="text-sm text-danger-700">
            テンプレ読込エラー: {templatesState.error.message}
          </p>
        )}
        {templatesState.status === 'ready' && templates.length === 0 && (
          <p className="text-sm text-slate-600">
            この地区には検証テンプレがありません。先にテンプレートを作成してください。
          </p>
        )}
        {templatesState.status === 'ready' && templates.length > 0 && (
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">templateId *</span>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              disabled={!canManage}
              aria-label="templateId"
              className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
            >
              <option value="">— 選択してください —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  [{t.status}] {t.displayName || '(無題)'} (v{t.version})
                </option>
              ))}
            </select>
          </label>
        )}
      </Card>

      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">2. 対象投稿 ID (postIds)</h2>
        <p className="text-xs text-slate-500 mb-2">
          投稿 ID をスペース / カンマ / 改行区切りで入力 (1 件以上)。 一覧から ID を取得する UI は別
          sprint。
        </p>
        <textarea
          value={postIdsText}
          onChange={(e) => setPostIdsText(e.target.value)}
          disabled={!canManage}
          rows={4}
          placeholder="例: post-abc, post-def, post-ghi"
          aria-label="postIds"
          className="w-full px-3 py-2 border border-slate-300 rounded text-xs font-mono"
          spellCheck={false}
        />
        <p className="mt-1 text-[11px] text-slate-500">{postIds.length} 件</p>
      </Card>

      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">3. 配分先 user UID (userIds)</h2>
        <p className="text-xs text-slate-500 mb-2">
          user UID をスペース / カンマ / 改行区切りで入力 (1 件以上)。
        </p>
        <textarea
          value={userIdsText}
          onChange={(e) => setUserIdsText(e.target.value)}
          disabled={!canManage}
          rows={4}
          placeholder="例: uid-1, uid-2, uid-3"
          aria-label="userIds"
          className="w-full px-3 py-2 border border-slate-300 rounded text-xs font-mono"
          spellCheck={false}
        />
        <p className="mt-1 text-[11px] text-slate-500">{userIds.length} 件</p>
      </Card>

      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">4. 配分戦略 (strategy)</h2>
        <div className="grid grid-cols-1 gap-2">
          {STRATEGY_OPTIONS.map((o) => (
            <label key={o.value} className="flex items-start gap-2 cursor-pointer text-sm">
              <input
                type="radio"
                name="strategy"
                value={o.value}
                checked={strategy === o.value}
                onChange={() => setStrategy(o.value)}
                disabled={!canManage}
                className="mt-1"
              />
              <span>
                <span className="font-medium">{o.label}</span>
                <span className="block text-[11px] text-slate-500">{o.help}</span>
              </span>
            </label>
          ))}
        </div>
      </Card>

      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">5. 制約 (任意)</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">maxAssignmentsPerUser (空欄=無制限)</span>
            <input
              type="number"
              value={maxPerUserText}
              onChange={(e) => setMaxPerUserText(e.target.value)}
              disabled={!canManage}
              min={1}
              aria-label="maxAssignmentsPerUser"
              className="min-h-touch px-3 border border-slate-300 rounded text-sm"
            />
          </label>
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">minAssignmentsPerPost (空欄=1)</span>
            <input
              type="number"
              value={minPerPostText}
              onChange={(e) => setMinPerPostText(e.target.value)}
              disabled={!canManage}
              min={1}
              aria-label="minAssignmentsPerPost"
              className="min-h-touch px-3 border border-slate-300 rounded text-sm"
            />
          </label>
        </div>
      </Card>

      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">6. AssignmentSet メタ (任意)</h2>
        <div className="grid grid-cols-1 gap-3">
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">displayName (省略時は自動生成)</span>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={!canManage}
              maxLength={120}
              aria-label="displayName"
              className="min-h-touch px-3 border border-slate-300 rounded text-sm"
            />
          </label>
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">description</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!canManage}
              rows={2}
              aria-label="description"
              className="px-3 py-2 border border-slate-300 rounded text-sm"
            />
          </label>
        </div>
      </Card>

      {validationError && (
        <Card padding="md" className="border-warning-600 mb-4">
          <p className="text-sm text-warning-700">⚠ {validationError}</p>
        </Card>
      )}

      <div className="flex items-center justify-between gap-2 mb-6 flex-wrap">
        <p className="text-sm text-slate-600">
          配分予定件数 (見積): <span className="font-bold">{estimatedCount}</span> 件
        </p>
        <Button
          onClick={handleSubmit}
          disabled={!canManage || !!validationError || distState.status === 'pending'}
        >
          {distState.status === 'pending' ? '配分中…' : '🎯 配分を実行'}
        </Button>
      </div>

      {distState.status === 'success' && distState.result && (
        <Card padding="lg" className="border-success-600 mb-4">
          <h3 className="text-sm font-bold text-success-800 mb-2">
            ✓ 配分完了 — assignmentSetId:{' '}
            <code className="text-xs">{distState.result.assignmentSetId}</code>
          </h3>
          <ul className="text-xs text-slate-700 grid grid-cols-1 gap-1">
            <li>戦略: {distState.result.strategy}</li>
            <li>合計 assignment 件数: {distState.result.assignmentCount}</li>
            <li>
              user 別件数:{' '}
              <code className="text-[11px]">{JSON.stringify(distState.result.perUserCount)}</code>
            </li>
            <li>
              post 別件数:{' '}
              <code className="text-[11px]">{JSON.stringify(distState.result.perPostCount)}</code>
            </li>
          </ul>
          <button
            type="button"
            onClick={reset}
            className="mt-3 text-xs text-primary-700 hover:underline"
          >
            再度配分する (form リセット不要)
          </button>
        </Card>
      )}

      {distState.status === 'error' && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">配分失敗: {distState.error.message}</p>
        </Card>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="配分を実行しますか?"
        message={`新規 AssignmentSet を作成し、${estimatedCount} 件の assignment を生成します。\n戦略: ${strategy}\npost: ${postIds.length} 件 / user: ${userIds.length} 件`}
        confirmLabel="実行"
        cancelLabel="キャンセル"
        intent="warning"
        onConfirm={() => void handleConfirm()}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
